// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
/* eslint-disable-next-line no-unused-vars */
import React from 'react';

import Input, {
  type InputProps,
} from '@zapier/design-system/src/components/forms/Input';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

/** @jsx isolateComponent */

export type TextInputProps = {|
  ...$Exact<InputProps>,
  /** Controls how text is automatically capitalized as it's entered
   * by the user
   */
  autoCapitalize?: 'on' | 'off' | 'words' | 'characters',
  /** Activates automatic correct while the user is editing the field
   * (used by Safari)
   */
  autoCorrect?: 'on' | 'off',
  /**
   * Whether the input accepts multiple lines of text.
   * Renders an `input` if `false` and a `textarea` if true`.
   */
  isMultiline: boolean,
  /**
   * Limits the maximum number of characters within the input.
   */
  maxLength?: number,
  /**
   * Indicates in which directions a `textarea` can be resized.
   * Only works when `isMultiline={true}`!
   */
  resize?: 'horizontal' | 'vertical' | 'both' | 'none',
  /**
   * Indicates how many rows should render by default for a `textarea`s.
   * Only works when `isMultiline={true}`!
   */
  rows?: number,
  /** Use spelling and grammar checks in the text input */
  spellCheck?: 'true' | 'false',
  /**
   * Indicates the type of the input so the browser can provide a better
   * experience to users. Only used for `input` fields and *not* `textarea`s,
   * so is affected by `isMultiline`.
   */
  type?: 'email' | 'password' | 'search' | 'tel' | 'text',
  /**
   * Should this component render in its full width?
   */
  isFullWidth: boolean,
|};

const Styles = labelStyles(__filename, {
  input: (props: TextInputProps) => [
    props.isMultiline &&
      withModifier('multiline', {
        height: 'auto',
      }),

    props.isFullWidth &&
      withModifier('fullwidth', {
        width: '100%',
      }),

    props.resize &&
      withModifier(`resize-${props.resize}`, {
        resize: props.resize,
      }),
  ],
});

/**
 * Renders either an `input` or `textarea` element, depending on the
 * `isMultiline` prop. This is a bare-bones implementation without
 * any special handling for events.
 *
 * Tags: core-ui, forms
 */
const TextInput = (props: TextInputProps) => {
  const Tag = props.isMultiline ? 'textarea' : 'input';
  return (
    <Input
      {...props}
      renderIconAfter={props.isMultiline ? undefined : props.renderIconAfter}
      renderIconBefore={props.isMultiline ? undefined : props.renderIconBefore}
    >
      {inputProps => (
        <Tag
          {...inputProps}
          autoCapitalize={props.autoCapitalize}
          autoCorrect={props.autoCorrect}
          css={[inputProps.css, Styles.input(props)]}
          rows={props.isMultiline ? props.rows : undefined}
          spellCheck={props.spellCheck}
          type={!props.isMultiline ? props.type : undefined}
          value={inputProps.value}
        />
      )}
    </Input>
  );
};

TextInput.defaultProps = {
  color: 'grey100',
  isMultiline: false,
  size: 'small',
  type: 'text',
  isFullWidth: false,
};

export default TextInput;
